import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTableStore } from "@/store/tableStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { TableCard } from "./TableCard";
import { QrCodeDialog } from "./QrCodeDialog";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { EditTableDialog } from "./EditTableDialog";
import { toast } from "@/hooks/use-toast";
import { StoreTable } from "@/models/tableModel";
import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";
import { Tabs, TabsTrigger, TabsList } from "@radix-ui/react-tabs";

export function TablesList() {
  const { t } = useTranslation();
  const { selectedStoreId } = useStoreManagementStore();
  const {
    tables,
    fetchTables,
    deleteTable,
    updateTable,
    setupRealtimeSubscription,
  } = useTableStore();
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [filterView, setFilterView] = useState<"all" | "active">("all");

  useEffect(() => {
    if (selectedStoreId) {
      // Fetch initial tables
      fetchTables(selectedStoreId);

      // Set up realtime subscription
      const cleanup = setupRealtimeSubscription(selectedStoreId);

      // Return cleanup function
      return () => {
        cleanup();
      };
    }
  }, [selectedStoreId, fetchTables, setupRealtimeSubscription]);

  const handleViewQR = (tableId: string) => {
    setSelectedTable(tableId);
    setShowQrDialog(true);
  };

  const handleDelete = (tableId: string) => {
    setSelectedTable(tableId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (selectedTable) {
      await deleteTable(selectedTable);
      setShowDeleteDialog(false);
    }
  };

  const handleEdit = async (tableData: { name: string; number: string }) => {
    if (selectedTable) {
      await updateTable(selectedTable, {
        name: tableData.name,
        number: parseInt(tableData.number, 10),
      });
    }
  };

  const filteredTables = tables.filter((table) => {
    if (filterView === "active") {
      return table.daily_scans > 0;
    }
    return true;
  });

  if (!selectedStoreId) {
    return (
      <div className="text-center p-8 border rounded-lg bg-muted/20">
        <p className="text-muted-foreground">
          {t("pleaseSelectStoreToManageTables")}
        </p>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="text-center p-8 border rounded-lg">
        <h3 className="font-semibold mb-2">{t("noTablesYet")}</h3>
        <p className="text-muted-foreground mb-4">
          {t("addTablesForYourStore")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <Tabs defaultValue="all" className="w-[400px] gap-4">
          <TabsList className="gap-2">
            <TabsTrigger
              className="border border-green-300 shadow-md rounded-md px-4 py-2"
              value="all"
              onClick={() => setFilterView("all")}
            >
              {t("allTables")}
            </TabsTrigger>
            <TabsTrigger
              className="border border-blue-300 shadow-md rounded-md px-4 py-2 ml-3"
              value="active"
              onClick={() => setFilterView("active")}
            >
              {t("activeTables")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
        {filteredTables.map((table) => (
          <TableCard
            key={table.id}
            table={table}
            onViewQR={() => handleViewQR(table.id)}
            onEdit={(data) => {
              setSelectedTable(table.id);
              setShowEditDialog(true);
            }}
            onDelete={() => handleDelete(table.id)}
          />
        ))}
      </div>

      {selectedTable && (
        <>
          <QrCodeDialog
            open={showQrDialog}
            onOpenChange={setShowQrDialog}
            tableId={selectedTable}
          />

          <ConfirmDeleteDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            onConfirm={confirmDelete}
            title={t("deleteTable")}
            description={t("deleteTableConfirmation")}
          />

          <EditTableDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            table={tables.find((t) => t.id === selectedTable) as StoreTable}
            onSave={handleEdit}
          />
        </>
      )}
    </div>
  );
}
